import { CHAIN_CONFIG, NATIVE_ADDRESS, formatWei } from "./config";

/**
 * Referral client.
 *
 * The scaffold this replaces minted a code from `crypto.getRandomValues` on
 * every page load, so a shared link was orphaned the moment the sharer
 * refreshed, and tracked progress in one in-memory object that nobody else
 * could see. Neither is fixable in the browser: a referral is a claim about
 * two people, made by neither of them, so the relayer holds it.
 */

export interface Progress {
  /** This wallet's shareable code. */
  code: string | null;
  /** The code that invited this wallet, if any. */
  invitedBy: string | null;
  wageredWei: string;
  requiredWei: string;
  /** The bar as a multiple of a Quick Hit card, straight from the relayer's
      policy constant so the offer copy cannot drift from the rule. */
  qualifyMultiple?: number;
  qualified: boolean;
  referrals: number;
  rewardsEarned: number;
}

/** localStorage key holding an inbound code until a wallet exists to bind it to. */
const PENDING_KEY = "scratchy:ref-pending";

/**
 * Remember a code from `?ref=` until there is a wallet to attach it to.
 *
 * Someone arriving on an invite link has no wallet connected and may not
 * connect one for days, so the code has to outlive the page view. Stored on
 * first sight and never overwritten — attribution is first-touch, and letting
 * a later link replace an earlier one would let anyone reassign a referral by
 * getting the player to open a different link.
 */
export function capturePendingCode(): string | null {
  try {
    const qs = new URLSearchParams(location.search);
    const inbound = (qs.get("ref") || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 12).toUpperCase();
    const existing = localStorage.getItem(PENDING_KEY);
    if (inbound && !existing) {
      localStorage.setItem(PENDING_KEY, inbound);
      return inbound;
    }
    return existing || inbound || null;
  } catch (e) {
    return null;
  }
}

export function pendingCode(): string | null {
  try {
    return localStorage.getItem(PENDING_KEY);
  } catch (e) {
    return null;
  }
}

/** Called once the relayer confirms the binding, so it is not sent forever. */
export function clearPendingCode(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (e) {}
}

export async function fetchProgress(account: string): Promise<Progress | null> {
  try {
    const res = await fetch(`${CHAIN_CONFIG.relayerUrl}/referral?address=${account}`);
    if (!res.ok) return null;
    return (await res.json()) as Progress;
  } catch (e) {
    /* Advisory only — the game must work with the referral service down. */
    return null;
  }
}

/** The invite link for a code, on this deployment. */
export function inviteLink(code: string): string {
  return `${location.origin}${location.pathname}?ref=${code}`;
}

/** "4,000 of 10,000 gwei" — progress toward the wagering bar. */
export function describeProgress(p: Progress): string {
  const done = BigInt(p.wageredWei);
  const need = BigInt(p.requiredWei);
  if (p.qualified) return "Qualified — your inviter has their free card";
  return `${formatWei(done)} of ${formatWei(need)} wagered`;
}

export { NATIVE_ADDRESS };

/**
 * The wagering bar, for the offer copy.
 *
 * Stated as a multiple of the card rather than as an absolute amount. Both are
 * the same rule, but "10x a Quick Hit card" is the one a reader can act on:
 * the absolute figure meant nothing without knowing what a card costs, and it
 * changes whenever a tier is repriced while the term itself does not.
 *
 * The multiple comes from the relayer's policy constant. The fallback matches
 * the static copy in index.html, which is what a visitor sees before the fetch
 * lands — referral-copy.test.ts pins both to QUALIFY_MULTIPLE so neither can
 * drift from the rule the relayer actually enforces.
 */
export const BAR_FALLBACK_MULTIPLE = 10;

export function describeBar(p: Progress): string {
  return `${p.qualifyMultiple ?? BAR_FALLBACK_MULTIPLE}× a Quick Hit card`;
}
